<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form>
        <div
          class="notifications_actions"
          v-if="hasUnreadNotifications || hasReadNotifications"
        >
          <button
            type="button"
            class="read_all_btn"
            @click="markAllAsRead"
            :disabled="isWaitingRequest"
            v-if="hasUnreadNotifications"
          >
            <i class="fas fa-check-double"></i>
            {{ $t("BUTTONS.readAllNotifications") }}
          </button>

          <button
            type="button"
            class="delete_read_btn"
            @click="openDeleteReadDialog"
            :disabled="isWaitingRequest"
            v-if="hasReadNotifications"
          >
            <i class="fal fa-trash-alt"></i>
            {{ $t("BUTTONS.deleteReadNotifications") }}
          </button>
        </div>

        <transition-group name="fade" v-if="receivedMessages.length">
          <div
            class="notification"
            :class="{
              read: message.read == true,
              clickable: isNotificationClickable(message),
            }"
            v-for="(message, index) in receivedMessages"
            :key="'k' + index"
            role="button"
            :tabindex="isNotificationClickable(message) ? 0 : -1"
            @click="handleNotificationClick(message)"
            @keyup.enter="handleNotificationClick(message)"
          >
            <h3>{{ message.title }}</h3>
            <p>{{ message.body }}</p>
            <div
              class="delete_notification"
              :class="{ read: message.read }"
              @click.stop="NotificationsReaded(message.id)"
              v-if="!message.read"
            >
              <i class="fas fa-check-double"></i>
            </div>
            <div
              class="delete_notification"
              :class="{ read: message.read }"
              style="cursor: default"
              v-if="message.read"
            >
              <i class="fas fa-check-double"></i>
            </div>
          </div>
        </transition-group>

        <p class="text-danger text-center text--darken-4 pt-5 pb-5" v-else>
          {{ $t("PLACEHOLDERS.no_notifications") }}
        </p>

        <!-- Start:: Pagination -->
        <template>
          <div class="pagination_container text-center mt-3 mb-0">
            <v-pagination
              class="py-0"
              square
              v-model="paginations.current_page"
              :length="paginations.last_page"
              :total-visible="6"
              @input="updateRouterQueryParam($event)"
              :prev-icon="
                getAppLocale == 'ar'
                  ? 'fal fa-angle-right'
                  : 'fal fa-angle-left'
              "
              :next-icon="
                getAppLocale == 'ar'
                  ? 'fal fa-angle-left'
                  : 'fal fa-angle-right'
              "
            />
          </div>
        </template>
        <!-- End:: Pagination -->
      </form>
    </div>
    <!-- END:: Single Step Form Content -->

    <v-dialog v-model="dialogDeleteRead">
      <v-card>
        <v-card-title class="text-h5 justify-center">{{
          $t("TITLES.deleteReadNotificationsConfirm")
        }}</v-card-title>
        <v-card-actions>
          <v-btn class="modal_confirm_btn" @click="deleteAllReadNotifications">{{
            $t("BUTTONS.yes")
          }}</v-btn>

          <v-btn class="modal_cancel_btn" @click="dialogDeleteRead = false">{{
            $t("BUTTONS.cancel")
          }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
export default {
  name: "CreateContact",

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      receivedMessages: [],

      dialogDeleteRead: false,

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
      notificationsData: "NotificationsModule/notificationsData",
    }),
    hasUnreadNotifications() {
      return this.notificationsData.unreadNotifications > 0;
    },
    hasReadNotifications() {
      return this.receivedMessages.some((message) => message.read);
    },
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.getData();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    ...mapActions({
      getNotifications: "NotificationsModule/getNotifications",
      markAllNotificationsRead: "NotificationsModule/markAllNotificationsRead",
      deleteReadNotifications: "NotificationsModule/deleteReadNotifications",
    }),
    openDeleteReadDialog() {
      this.dialogDeleteRead = true;
    },
    async deleteAllReadNotifications() {
      if (this.isWaitingRequest) return;

      this.isWaitingRequest = true;

      try {
        const res = await this.deleteReadNotifications();
        this.$message.success(
          res?.data?.message || this.$t("MESSAGES.deletedSuccessfully")
        );
        this.dialogDeleteRead = false;
        await this.getData();
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.data?.errors ||
            this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.isWaitingRequest = false;
      }
    },
    async markAllAsRead() {
      if (this.isWaitingRequest) return;

      this.isWaitingRequest = true;

      try {
        const res = await this.markAllNotificationsRead();
        this.$message.success(res.data.message);
        this.receivedMessages.forEach((message) => {
          message.read = true;
        });
        await this.getData();
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.data?.errors ||
            this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.isWaitingRequest = false;
      }
    },
    async handleNotificationClick(message) {
      const targetRoute = this.getNotificationRoute(message);

      if (targetRoute) {
        if (!message.read) {
          await this.NotificationsReaded(message.id);
        }

        this.$router.push(targetRoute);
      }
    },
    isNotificationClickable(message) {
      return Boolean(this.getNotificationRoute(message));
    },
    getNotificationRoute(message) {
      const type = String(message?.data?.type || "").toLowerCase();
      const id = message?.data?.id;
      const combinedText = `${message?.title || ""} ${
        message?.body || ""
      }`.toLowerCase();

      switch (type) {
        case "contact_us":
          return `/contact-messages/all`;
        case "new_client":
          return `/clients/show/${id}`;
        case "new_influencer":
          return `/providers/show/${id}`;
        case "new booking":
        case "new_booking":
        case "update_booking":
        case "booking_status":
        case "extend_booking":
        case "pick_up_booking":
        case "booking_golden_unit":
        case "booking_provider_unit":
        case "distinction":
        case "add_unit":
          return `/bookings/show/${id}`;
        case "wallet":
          return `/delivery-wallet-requests?id=${id}`;
        case "wallet_client":
          return `/delivery-wallet-requests?id=${id}`;
        case "wallet_provider":
          return `/provider-wallet-requests?id=${id}`;
        case "delete_account":
          return "/app-content/delete-account";
        case "new registration":
          if (
            combinedText.includes("عميل") ||
            combinedText.includes("client")
          ) {
            return `/clients/show/${id}`;
          }

          if (
            combinedText.includes("مقدم خدمة") ||
            combinedText.includes("provider") ||
            combinedText.includes("service provider")
          ) {
            return `/providers/show/${id}`;
          }

          return null;
        default:
          return null;
      }
    },
    async getData() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "notifications/get-notifications-admin",
          params: {
            page: this.paginations.current_page,
          },
        });
        this.receivedMessages = res.data.data;
        this.paginations.last_page = res.data.meta.last_page;
        this.paginations.items_per_page = res.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },

    async NotificationsReaded(item_id) {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `notifications/notification-read?notification_id=${item_id}`,
          // data: REQUEST_DATA,
        });

        this.$message.success(res.data.message);
        // Update the local state for the specific message
        const message = this.receivedMessages.find((msg) => msg.id === item_id);
        if (message) {
          message.read = true;
        }

        this.getData();
        this.getNotifications();
        return true;
      } catch (error) {
        this.$message.error(error.response.data.errors);
        return false;
      }
    },
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
  },

  created() {
    this.getData();

    navigator.serviceWorker.addEventListener("message", (event) => {
      const receivedMessage = event.data.data;

      this.receivedMessages.unshift(receivedMessage);
      this.getData();
      this.getNotifications();

      console.log(event.data.data);
      // console.log("receivedMessage", event)
      // Update component state or display the received message in the UI
    });

    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to

/* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
.show_order {
  cursor: pointer;
  i {
    font-size: 20px;
    position: absolute;
    top: 20px;
    left: 60px;
  }
}
.notification {
  background: #000;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 20px;
  position: relative;

  &.clickable {
    cursor: pointer;
  }

  .delete_notification {
    position: absolute;
    z-index: 2 !important;
    top: 20px;
    left: 20px;
    cursor: pointer;

    &.read {
      i {
        color: #49a956;
      }
    }

    i {
      font-size: 20px;
      color: #ddd;
    }
  }
}

.text-danger {
  font-size: 22px;
}

.notifications_actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 20px;

  .read_all_btn,
  .delete_read_btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: none;
    border-radius: 8px;
    color: #fff;
    font-size: 15px;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .read_all_btn {
    background: #49a956;
  }

  .delete_read_btn {
    background: #d9534f;
  }
}
</style>
